import { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  useGetShopsQuery,
  useCreateShopMutation,
  useUpdateShopMutation,
  useDeleteShopMutation,
} from '../store/services/shops';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import CategorySelect from '../components/ShopCategories/CategorySelect';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  address: yup.string().required('Address is required'),
  contact_number: yup.string().required('Contact number is required'),
  category_id: yup.string().required('Category is required'),
});

export default function Shops() {
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [selectedShop, setSelectedShop] = useState<any>(null);
  
  const { data: shops, isLoading, refetch } = useGetShopsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  
  const [createShop] = useCreateShopMutation();
  const [updateShop] = useUpdateShopMutation();
  const [deleteShop] = useDeleteShopMutation();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const formik = useFormik({
    initialValues: {
      name: '',
      address: '',
      contact_number: '',
      category_id: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedShop) {
          await updateShop({
            id: selectedShop._id,
            data: values,
          }).unwrap();
          setSnackbar({ open: true, message: 'Shop updated successfully', severity: 'success' });
        } else {
          await createShop(values).unwrap();
          setSnackbar({ open: true, message: 'Shop created successfully', severity: 'success' });
        }
        handleClose();
        refetch(); // Force a refetch after mutation
      } catch (error) {
        console.error('Failed to save shop:', error);
        setSnackbar({ open: true, message: 'Failed to save shop', severity: 'error' });
      }
    },
  });

  const handleClickOpen = () => {
    setSelectedShop(null);
    formik.resetForm();
    setOpen(true);
  };

  const handleEdit = (shop: any) => {
    setSelectedShop(shop);
    formik.setValues({
      name: shop.name,
      address: shop.address,
      contact_number: shop.contact_number,
      category_id: shop.category?._id || shop.category_id || '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this shop?')) {
      try {
        await deleteShop(id).unwrap();
        setSnackbar({ open: true, message: 'Shop deleted successfully', severity: 'success' });
        refetch(); // Force a refetch after deletion
      } catch (error) {
        console.error('Failed to delete shop:', error);
        setSnackbar({ open: true, message: 'Failed to delete shop', severity: 'error' });
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      {currentUser?.role === 'SuperAdmin' && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleClickOpen}>
            Add Shop
          </Button>
        </Box>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Contact Number</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              {currentUser?.role === 'SuperAdmin' && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {shops?.map((shop: any) => (
              <TableRow key={shop._id}>
                <TableCell>{shop.name}</TableCell>
                <TableCell>{shop.address}</TableCell>
                <TableCell>{shop.contact_number}</TableCell>
                <TableCell>{shop.category?.name || 'N/A'}</TableCell>
                <TableCell>
                  <Chip
                    label={shop.is_active ? 'Active' : 'Inactive'}
                    color={shop.is_active ? 'success' : 'error'}
                  />
                </TableCell>
                {currentUser?.role === 'SuperAdmin' && (
                  <TableCell>
                    <IconButton onClick={() => handleEdit(shop)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(shop._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{selectedShop ? 'Edit Shop' : 'Add Shop'}</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <CategorySelect
              value={formik.values.category_id}
              onChange={(value) => formik.setFieldValue('category_id', value)}
              error={formik.touched.category_id && formik.errors.category_id ? String(formik.errors.category_id) : undefined}
            />
            <TextField
              fullWidth
              margin="dense"
              id="address"
              name="address"
              label="Address"
              multiline
              rows={3}
              value={formik.values.address}
              onChange={formik.handleChange}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />
            <TextField
              fullWidth
              margin="dense"
              id="contact_number"
              name="contact_number"
              label="Contact Number"
              value={formik.values.contact_number}
              onChange={formik.handleChange}
              error={formik.touched.contact_number && Boolean(formik.errors.contact_number)}
              helperText={formik.touched.contact_number && formik.errors.contact_number}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedShop ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 